/* eslint-disable react-hooks/set-state-in-effect -- Mount flag is set once in an effect to gate SSR-vs-client language rendering and avoid hydration mismatch. */
import { ChevronDownIcon } from 'lucide-react';

import { useEffect, useRef, useState } from 'react';

import {
  SUPPORTED_LANGUAGES,
  getEnabledLanguageInfo,
  useNamespace,
  useTranslation,
} from '@splitfiesta/i18n';
import { Button } from '@splitfiesta/ui';

import { CheckmarkIcon } from '@/components/ui/CheckmarkIcon';

import { useTypedApi } from '@/hooks/useTypedApi';

import { useAuthStore } from '@/stores/authStore';

import { toast } from '@/utils/toast';

interface LanguageSwitchProps {
  variant?: 'button' | 'clean' | 'auth';
}

export function LanguageSwitch({ variant = 'button' }: LanguageSwitchProps) {
  const { t, i18n } = useTranslation();
  useNamespace('common');

  const user = useAuthStore(state => state.user);
  const api = useTypedApi();
  const [isOpen, setIsOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [saving, setSaving] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Use default language during SSR to avoid hydration mismatch
  const currentLanguage = mounted ? i18n.language : 'en';
  const currentLangInfo = SUPPORTED_LANGUAGES.find(
    lang => lang.code === currentLanguage
  );
  const currentName = currentLangInfo?.nativeName || currentLanguage;
  const currentCode = currentLanguage.toUpperCase();

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleLanguageChange = async (language: string) => {
    setIsOpen(false);

    // Change UI language immediately
    await i18n.changeLanguage(language);

    // Save to database if user is logged in
    if (user) {
      try {
        setSaving(true);
        await api.user.updateSettings({ language });
      } catch {
        toast.warning(t('common:settings.saveLanguageError'));
      } finally {
        setSaving(false);
      }
    }
  };

  const availableLanguages = getEnabledLanguageInfo().map(lang => ({
    code: lang.code,
    name: lang.nativeName,
  }));

  const chevronIcon = saving ? (
    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400" />
  ) : (
    <ChevronDownIcon
      className={`w-5 h-5 ${
        variant === 'auth'
          ? 'text-white/80'
          : 'text-gray-400 dark:text-gray-500'
      }`}
    />
  );

  // Use plain button for "clean" variant to avoid Button component's extra styling
  const renderTriggerButton = () => {
    if (variant === 'clean') {
      return (
        <button
          onClick={() => setIsOpen(!isOpen)}
          data-testid="language-switch-button"
          data-state={isOpen ? 'open' : 'closed'}
          className="inline-flex items-center space-x-1 sm:space-x-2 text-sm font-medium min-w-max px-2 py-2 rounded-full menu-btn text-gray-700 dark:text-gray-300"
        >
          <span className="hidden sm:inline whitespace-nowrap">
            {currentName}
          </span>
          <span className="sm:hidden whitespace-nowrap">{currentCode}</span>
          {chevronIcon}
        </button>
      );
    }

    return (
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        data-testid="language-switch-button"
        className={`inline-flex items-center space-x-2 text-sm font-medium min-w-max ${
          variant === 'button'
            ? 'px-4 py-2 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-full shadow-xs border border-gray-200/50 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100'
            : 'px-3 py-2 rounded-full border-2 bg-white/10 backdrop-blur-xs border-white/20 hover:bg-white/20 hover:border-orange-400 text-white/90 hover:text-white'
        }`}
        rightIcon={chevronIcon}
      >
        <span className="whitespace-nowrap">{currentName}</span>
      </Button>
    );
  };

  return (
    <div className="relative" ref={menuRef}>
      {renderTriggerButton()}

      {isOpen && (
        <div className="absolute right-0 mt-2 w-max bg-white dark:bg-gray-800 rounded-lg shadow-lg ring-1 ring-black/5 dark:ring-gray-700 overflow-hidden z-50">
          <div className="py-1 whitespace-nowrap">
            {availableLanguages.map(lang => (
              <button
                key={lang.code}
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleLanguageChange(lang.code);
                }}
                disabled={saving}
                data-testid={`language-option-${lang.code}`}
                className="w-full flex items-center pl-2 pr-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-[#F6F5F3] dark:hover:bg-gray-700/50 transition-colors text-left"
              >
                {currentLanguage === lang.code ? (
                  <CheckmarkIcon className="w-4 h-4 text-orange-500 dark:text-orange-400 shrink-0 mr-1.5" />
                ) : (
                  <div className="w-4 h-4 mr-1.5" />
                )}
                <span>{lang.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
